import { supabase } from './supabase';
import type { Database } from '@/types/supabase';
import type { UserProfile, Assessment, UserSymptom } from '@/types/user';

type Tables = Database['public']['Tables'];
type Profile = Tables['profiles']['Row'];
type AssessmentRow = Tables['assessments']['Row'];

// Profiles
export async function createUserProfile(userId: string, data: Partial<UserProfile>) {
  const { error } = await supabase
    .from('profiles')
    .insert({
      id: userId,
      email: data.email,
      display_name: data.displayName,
      energy_type: data.energyType,
    });

  if (error) throw error;
}

export async function updateUserProfile(userId: string, data: Partial<UserProfile>) {
  const { error } = await supabase
    .from('profiles')
    .update({
      display_name: data.displayName,
      energy_type: data.energyType,
      photo_url: data.photoUrl,
      birth_date: data.birthDate,
      birth_time: data.birthTime,
      birth_place: data.birthPlace,
      notification_preferences: data.notification_preferences
    })
    .eq('id', userId);

  if (error) throw error;
}

export async function deleteUserAccount(userId: string) {
  try {
    // 1. Delete the user's profile first (this will cascade to assessments)
    const { error: profileError } = await supabase
      .from('profiles')
      .delete()
      .eq('id', userId);
    
    if (profileError) throw profileError;

    // 2. Delete the auth user using the service role client
    const { error: authError } = await supabase.auth.admin.deleteUser(
      userId,
      true // purge data
    );
    
    if (authError) throw authError;

    return true;
  } catch (error) {
    console.error('Error deleting user account:', error);
    throw error;
  }
}

export async function uploadProfilePhoto(userId: string, file: File): Promise<string> {
  const fileExt = file.name.split('.').pop();
  const fileName = `${userId}-${Math.random()}.${fileExt}`;
  const filePath = `profile-photos/${fileName}`;

  const { error: uploadError } = await supabase.storage
    .from('avatars')
    .upload(filePath, file);

  if (uploadError) throw uploadError;

  const { data: { publicUrl } } = supabase.storage
    .from('avatars')
    .getPublicUrl(filePath);

  return publicUrl;
}

export async function getUserProfile(userId: string): Promise<UserProfile | null> {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', userId)
    .maybeSingle();

  if (error) {
    // If the error is "no rows returned", return null instead of throwing
    if (error.code === 'PGRST116') {
      return null;
    }
    throw error;
  }

  if (!data) return null;

  return {
    id: data.id,
    email: data.email,
    displayName: data.display_name || undefined,
    energyType: data.energy_type,
    createdAt: new Date(data.created_at),
    lastAssessment: data.last_assessment ? new Date(data.last_assessment) : undefined,
    photoUrl: data.photo_url,
    birthDate: data.birth_date,
    birthTime: data.birth_time,
    birthPlace: data.birth_place,
    notification_preferences: data.notification_preferences
  };
}

// Assessments
export async function createAssessment(assessment: Omit<Assessment, 'id' | 'date'>) {
  const { error } = await supabase
    .from('assessments')
    .insert({
      user_id: assessment.userId,
      zone: assessment.zone,
      overall_energy: assessment.overallEnergy,
      affected_centers: assessment.affectedCenters || [],
      symptoms: assessment.symptoms,
      response: assessment.response,
      notes: assessment.notes,
    });

  if (error) throw error;
}

export async function getUserAssessments(userId: string): Promise<Assessment[]> {
  const { data, error } = await supabase
    .from('assessments')
    .select('*')
    .eq('user_id', userId)
    .order('date', { ascending: false });

  if (error) throw error;

  return (data || []).map((row: AssessmentRow): Assessment => ({
    id: row.id,
    userId: row.user_id,
    date: new Date(row.date),
    zone: row.zone,
    overallEnergy: row.overall_energy,
    affectedCenters: row.affected_centers || [],
    symptoms: row.symptoms || [],
    response: row.response || '',
    notes: row.notes || undefined,
  }));
}

// User Symptoms
export async function createUserSymptom(symptom: Omit<UserSymptom, 'id' | 'createdAt' | 'updatedAt' | 'adminReviewed' | 'adminApproved' | 'adminNotes'>) {
  const { data, error } = await supabase
    .from('user_symptoms')
    .insert({
      user_id: symptom.userId,
      text: symptom.text,
      description: symptom.description,
      energy_type: symptom.energyType,
      is_private: symptom.isPrivate
    })
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function getUserSymptoms(userId: string) {
  const { data, error } = await supabase
    .from('user_symptoms')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data;
}

export async function getPublicSymptoms() {
  const { data, error } = await supabase
    .from('user_symptoms')
    .select('*')
    .eq('is_private', false)
    .eq('admin_approved', true)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data;
}

export async function updateUserSymptom(symptomId: string, updates: Partial<UserSymptom>) {
  const { error } = await supabase
    .from('user_symptoms')
    .update({
      text: updates.text,
      description: updates.description,
      is_private: updates.isPrivate
    })
    .eq('id', symptomId);

  if (error) throw error;
}

export async function deleteUserSymptom(symptomId: string) {
  const { error } = await supabase
    .from('user_symptoms')
    .delete()
    .eq('id', symptomId);

  if (error) throw error;
}

// Admin functions
export async function reviewUserSymptom(symptomId: string, approved: boolean, notes?: string) {
  const { error } = await supabase
    .from('user_symptoms')
    .update({
      admin_reviewed: true,
      admin_approved: approved,
      admin_notes: notes
    })
    .eq('id', symptomId);

  if (error) throw error;
}

export async function getAllUserSymptoms() {
  const { data, error } = await supabase
    .from('user_symptoms')
    .select(`
      *,
      profiles:user_id (
        email,
        display_name
      )
    `)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data;
}